/**
 * Iter-193g — Admin: a user's full expense-notes history.
 *
 * Accessible to CEO/ADMIN/GESTIONNAIRE at /admin/users/:userId/expenses. Lists
 * every monthly note (DRAFT / SUBMITTED / PAID) sorted by recency. Clicking a
 * note expands its line items inline and surfaces CSV + PDF export buttons.
 * For SUBMITTED notes the CEO/GESTIONNAIRE gets a "Mark paid" CTA.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import CrossMonthSearchPanel from "../components/CrossMonthSearchPanel";
import {
  ArrowLeft, Banknote, ChevronDown, ChevronRight, Download, Loader2, Paperclip, RefreshCw, ScanLine,
} from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
const STATUS_LABEL = { DRAFT: "Brouillon", SUBMITTED: "Soumise", VALIDATED: "Validée", PAID: "Réglée", REJECTED: "Rejetée" };
const STATUS_TONE = {
  DRAFT: "bg-zinc-100 text-zinc-700",
  SUBMITTED: "bg-amber-100 text-amber-900",
  VALIDATED: "bg-blue-100 text-blue-900",
  PAID: "bg-emerald-100 text-emerald-900",
  REJECTED: "bg-red-100 text-red-900",
};

export default function AdminUserExpenses() {
  const { userId } = useParams();
  const { user } = useAuth();
  const canValidate = user && (user.role === "CEO" || user.role === "GESTIONNAIRE");
  const [target, setTarget] = useState(null);
  const [notes, setNotes] = useState([]);
  const [openNoteId, setOpenNoteId] = useState(null);
  const [openLines, setOpenLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, n] = await Promise.all([
        api.get(`/users/${userId}`).catch(() => ({ data: null })),
        api.get(`/expenses/admin/users/${userId}/notes`),
      ]);
      setTarget(u.data);
      setNotes(n.data.notes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const toggle = async (noteId) => {
    if (openNoteId === noteId) {
      setOpenNoteId(null);
      setOpenLines([]);
      return;
    }
    setOpenNoteId(noteId);
    setLoadingLines(true);
    try {
      const r = await api.get(`/expenses/admin/users/${userId}/notes/${noteId}`);
      setOpenLines(r.data.lines || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoadingLines(false);
    }
  };

  const download = async (noteId, kind, ymLabel) => {
    try {
      const r = await api.get(`/expenses/notes/${noteId}/export.${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NDF-${ymLabel}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec export");
    }
  };

  const markPaid = async (noteId) => {
    if (!confirm("Marquer cette NDF comme payée ?")) return;
    try {
      await api.patch(`/expenses/notes/${noteId}/mark-paid`);
      toast.success("NDF marquée payée");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    }
  };

  const monthLabel = (y, m) => {
    const labels = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
    return `${labels[m - 1]} ${y}`;
  };

  return (
    <div className="px-4 sm:px-6 py-6 max-w-5xl mx-auto" data-testid="admin-user-expenses-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to={`/admin/users/${userId}`} className="text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 text-xs font-mono">
              <ArrowLeft size={12} /> Profil utilisateur
            </Link>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight inline-flex items-center gap-2">
            <ScanLine size={20} className="text-zinc-700" />
            Historique notes de frais
          </h1>
          {target && (
            <p className="text-sm text-zinc-500 mt-1">{target.name} · <span className="font-mono">{target.email}</span></p>
          )}
        </div>
        <button onClick={load} disabled={loading} data-testid="admin-user-expenses-refresh"
          className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-zinc-700 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      {/* Iter-228x-bis — Cross-month search over this user's NDF lines. */}
      <div className="mb-4">
        <CrossMonthSearchPanel
          kind="user"
          target_user_id={userId}
          testidPrefix="adm-user-search"
          csvFilenamePrefix={`ndf_user_${userId}`}
          categories={["Restauration","Carburant","Hôtel","Transport","Alimentation","Électronique","Loisirs","Abonnements","Loyer","Licences logicielles","Honoraires","Fournitures","Marketing","Banque","Comptabilité","Juridique","Salaires","Charges sociales","Télécom & Internet","Formation","Assurances","Autre"]}
        />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {notes.length === 0 && !loading && (
          <div className="px-4 py-14 text-center text-sm text-zinc-500" data-testid="admin-user-expenses-empty">
            Aucune note de frais pour cet utilisateur.
          </div>
        )}
        {notes.map((n) => {
          const isOpen = openNoteId === n.expense_note_id;
          const ym = `${n.year}-${String(n.month).padStart(2, "0")}`;
          return (
            <div key={n.expense_note_id} className="border-b border-zinc-100 last:border-b-0" data-testid={`admin-user-expense-note-${n.expense_note_id}`}>
              <button
                type="button"
                onClick={() => toggle(n.expense_note_id)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50/60 text-left"
              >
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="font-mono text-sm font-medium w-32">{monthLabel(n.year, n.month)}</span>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono uppercase ${STATUS_TONE[n.status]}`}>
                  {STATUS_LABEL[n.status] || n.status}
                </span>
                {/* Iter-236 — payment proof badge in the collaborator history. */}
                {n.status === "PAID" && n.payment_proof?.drive_web_view_link && (
                  <a
                    href={n.payment_proof.drive_web_view_link}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`admin-user-expense-proof-${n.expense_note_id}`}
                    title={`Justificatif de règlement : ${n.payment_proof.filename || ""}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                  >
                    <Paperclip size={10} /> Justificatif
                  </a>
                )}
                <span className="text-xs text-zinc-500 font-mono ml-2">{n.n_lines} ligne(s)</span>
                <span className="text-xs text-zinc-900 font-mono ml-auto font-semibold">{fmt(n.total_mad)} MAD</span>
              </button>
              {isOpen && (
                <div className="px-4 py-3 bg-zinc-50 border-t border-zinc-100">
                  <div className="flex justify-end gap-2 mb-3 flex-wrap">
                    <button onClick={() => download(n.expense_note_id, "pdf", `${target?.name?.replace(/\s+/g, "_")}-${ym}`)}
                      data-testid={`admin-user-expense-pdf-${n.expense_note_id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 bg-white text-zinc-700 text-xs rounded-md hover:bg-zinc-50">
                      <Download size={12} /> PDF
                    </button>
                    <button onClick={() => download(n.expense_note_id, "csv", `${target?.name?.replace(/\s+/g, "_")}-${ym}`)}
                      data-testid={`admin-user-expense-csv-${n.expense_note_id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 bg-white text-zinc-700 text-xs rounded-md hover:bg-zinc-50">
                      <Download size={12} /> CSV
                    </button>
                    {canValidate && n.status === "SUBMITTED" && (
                      <button onClick={() => markPaid(n.expense_note_id)}
                        data-testid={`admin-user-expense-mark-paid-${n.expense_note_id}`}
                        className="inline-flex items-center gap-1.5 px-3 h-8 bg-emerald-600 text-white text-xs rounded-md hover:bg-emerald-700">
                        <Banknote size={12} /> Marquer payée
                      </button>
                    )}
                  </div>
                  {loadingLines ? (
                    <div className="flex items-center gap-2 text-zinc-500 text-sm py-4">
                      <Loader2 size={12} className="animate-spin" /> Chargement…
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm bg-white rounded border border-zinc-200">
                        <thead className="bg-zinc-100">
                          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                            <th className="px-3 py-2">Date</th>
                            <th className="px-3 py-2">Fournisseur</th>
                            <th className="px-3 py-2">N° Facture</th>
                            <th className="px-3 py-2">Catégorie</th>
                            <th className="px-3 py-2 text-right">Montant</th>
                            <th className="px-3 py-2">Devise</th>
                            <th className="px-3 py-2 text-right">Conv. MAD</th>
                          </tr>
                        </thead>
                        <tbody>
                          {openLines.length === 0 && (
                            <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500 text-xs">Aucune ligne.</td></tr>
                          )}
                          {openLines.map((l) => (
                            <tr key={l.expense_line_id} className="border-t border-zinc-100">
                              <td className="px-3 py-2 font-mono text-xs">{l.invoice_date}</td>
                              <td className="px-3 py-2 text-sm">{l.vendor}</td>
                              <td className="px-3 py-2 font-mono text-xs text-zinc-600">{l.invoice_number || "—"}</td>
                              <td className="px-3 py-2 text-xs">{l.category}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{fmt(l.amount)}</td>
                              <td className="px-3 py-2 font-mono text-xs">{l.currency}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{fmt(l.amount_mad)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
